import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  ShieldCheck, UserPlus, KeyRound, Lock, Unlock, LogOut, Trash2, Users, SlidersHorizontal,
  Database, Activity, ScrollText, UserCog, Download, RefreshCw, Search, Mail, ShieldAlert,
  Upload, RotateCcw, AlertTriangle, HardDrive, Sparkles, Copy, Clock,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const cfg = { withCredentials: true };

const errMsg = (e, fb) => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(' ');
  return fb || 'Something went wrong';
};
const fmtBytes = (b) => {
  if (b == null || b < 0) return '—';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0; let n = b;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i += 1; }
  return `${n.toFixed(1)} ${u[i]}`;
};
const fmtUptime = (s) => {
  if (s == null) return '—';
  const d = Math.floor(s / 86400); const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${d ? `${d}d ` : ''}${h}h ${m}m`;
};

// ─────────────────────────── MY ACCOUNT ───────────────────────────
const MyAccountTab = () => {
  const { user, checkAuth } = useAuth();
  const [email, setEmail] = useState('');
  const [newPw, setNewPw] = useState(''); const [newPw2, setNewPw2] = useState('');
  const [newPin, setNewPin] = useState(''); const [newPin2, setNewPin2] = useState('');
  const [busy, setBusy] = useState('');

  const changeEmail = async (e) => {
    e.preventDefault(); setBusy('email');
    try {
      await axios.post(`${API}/api/admin/control/account/change-email`, { new_email: email }, cfg);
      toast.success('Login email updated'); setEmail(''); await checkAuth?.();
    } catch (er) { toast.error(errMsg(er, 'Could not change email')); } finally { setBusy(''); }
  };
  const changePw = async (e) => {
    e.preventDefault();
    if (newPw !== newPw2) { toast.error('New passwords do not match'); return; }
    setBusy('pw');
    try {
      await axios.post(`${API}/api/admin/control/account/change-password`, { new_password: newPw }, cfg);
      toast.success('Password updated'); setNewPw(''); setNewPw2('');
    } catch (er) { toast.error(errMsg(er, 'Could not change password')); } finally { setBusy(''); }
  };
  const changePin = async (e) => {
    e.preventDefault();
    if (newPin !== newPin2) { toast.error('New PINs do not match'); return; }
    setBusy('pin');
    try {
      await axios.post(`${API}/api/admin/control/account/change-pin`, { new_pin: newPin }, cfg);
      toast.success('PIN updated'); setNewPin(''); setNewPin2('');
    } catch (er) { toast.error(errMsg(er, 'Could not change PIN')); } finally { setBusy(''); }
  };

  const card = 'bg-white rounded-2xl border border-[#E8E5DF] p-6';
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5" data-testid="cc-my-account">
      <form onSubmit={changeEmail} className={card}>
        <div className="flex items-center gap-2 mb-1"><Mail className="w-4 h-4 text-[#FF3B6B]" /><h3 className="font-semibold text-[#1E1E24]">Change User ID (Email)</h3></div>
        <p className="text-xs text-[#6B6B7A] mb-4">Current: <span className="font-mono">{user?.email}</span></p>
        <Label className="text-xs">New email</Label>
        <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required data-testid="cc-email-new" className="mb-4 mt-1 rounded-xl" placeholder="new@email.com" />
        <Button type="submit" disabled={busy === 'email'} data-testid="cc-email-submit" className="w-full rounded-full bg-[#1E1E24] text-white">{busy === 'email' ? 'Saving…' : 'Update Email'}</Button>
      </form>

      <form onSubmit={changePw} className={card}>
        <div className="flex items-center gap-2 mb-1"><Lock className="w-4 h-4 text-[#FF3B6B]" /><h3 className="font-semibold text-[#1E1E24]">Change Password</h3></div>
        <p className="text-xs text-[#6B6B7A] mb-4">At least 8 characters. No current password needed.</p>
        <Label className="text-xs">New password</Label>
        <Input value={newPw} onChange={(e) => setNewPw(e.target.value)} type="password" required data-testid="cc-pw-new" className="mb-3 mt-1 rounded-xl" />
        <Label className="text-xs">Confirm new password</Label>
        <Input value={newPw2} onChange={(e) => setNewPw2(e.target.value)} type="password" required data-testid="cc-pw-confirm" className="mb-4 mt-1 rounded-xl" />
        <Button type="submit" disabled={busy === 'pw'} data-testid="cc-pw-submit" className="w-full rounded-full bg-[#1E1E24] text-white">{busy === 'pw' ? 'Saving…' : 'Update Password'}</Button>
      </form>

      <form onSubmit={changePin} className={card}>
        <div className="flex items-center gap-2 mb-1"><KeyRound className="w-4 h-4 text-[#06D6A0]" /><h3 className="font-semibold text-[#1E1E24]">Change 4-digit PIN</h3></div>
        <p className="text-xs text-[#6B6B7A] mb-4">Used to authorise sensitive actions. No current PIN needed.</p>
        <Label className="text-xs">New PIN</Label>
        <Input value={newPin} onChange={(e) => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 4))} type="password" inputMode="numeric" required data-testid="cc-pin-new" className="mb-3 mt-1 rounded-xl font-mono tracking-[0.3em]" placeholder="••••" />
        <Label className="text-xs">Confirm new PIN</Label>
        <Input value={newPin2} onChange={(e) => setNewPin2(e.target.value.replace(/\D/g, '').slice(0, 4))} type="password" inputMode="numeric" required data-testid="cc-pin-confirm" className="mb-4 mt-1 rounded-xl font-mono tracking-[0.3em]" placeholder="••••" />
        <Button type="submit" disabled={busy === 'pin'} data-testid="cc-pin-submit" className="w-full rounded-full bg-[#06D6A0] text-white">{busy === 'pin' ? 'Saving…' : 'Update PIN'}</Button>
      </form>
    </div>
  );
};

// ─────────────────────────── USERS & ACCESS ───────────────────────────
const CreateUserDialog = ({ open, onClose, onCreated }) => {
  const [f, setF] = useState({ name: '', email: '', password: '', role: 'employee', employee_role: 'manager', pin: '', is_super_admin: false, force_password_change: true });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    setBusy(true);
    try {
      const payload = { ...f };
      if (f.role !== 'employee') delete payload.employee_role;
      if (!f.pin) delete payload.pin;
      await axios.post(`${API}/api/admin/control/users`, payload, cfg);
      toast.success('User created'); onCreated?.(); onClose?.();
      setF({ name: '', email: '', password: '', role: 'employee', employee_role: 'manager', pin: '', is_super_admin: false, force_password_change: true });
    } catch (er) { toast.error(errMsg(er, 'Could not create user')); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg bg-white" data-testid="cc-create-user-dialog">
        <DialogHeader><DialogTitle>Create User</DialogTitle><DialogDescription>Add a new admin, manager or staff login.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Name</Label><Input value={f.name} onChange={(e) => set('name', e.target.value)} data-testid="cc-new-name" className="mt-1 rounded-xl" /></div>
            <div><Label className="text-xs">Email</Label><Input value={f.email} onChange={(e) => set('email', e.target.value)} type="email" data-testid="cc-new-email" className="mt-1 rounded-xl" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Temp password</Label><Input value={f.password} onChange={(e) => set('password', e.target.value)} data-testid="cc-new-password" className="mt-1 rounded-xl" placeholder="min 8 chars" /></div>
            <div><Label className="text-xs">PIN (optional)</Label><Input value={f.pin} onChange={(e) => set('pin', e.target.value.replace(/\D/g, '').slice(0, 4))} data-testid="cc-new-pin" className="mt-1 rounded-xl font-mono" placeholder="4 digits" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Role</Label>
              <Select value={f.role} onValueChange={(v) => set('role', v)}>
                <SelectTrigger className="mt-1 rounded-xl" data-testid="cc-new-role"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="admin">Admin</SelectItem><SelectItem value="employee">Employee</SelectItem></SelectContent>
              </Select>
            </div>
            {f.role === 'employee' && (
              <div>
                <Label className="text-xs">Employee role</Label>
                <Select value={f.employee_role} onValueChange={(v) => set('employee_role', v)}>
                  <SelectTrigger className="mt-1 rounded-xl" data-testid="cc-new-emprole"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="manager">Manager</SelectItem><SelectItem value="party_host">Party Host</SelectItem><SelectItem value="staff">Staff</SelectItem></SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div className="flex items-center justify-between rounded-xl border border-[#E8E5DF] p-3">
            <span className="text-sm">Grant Super Admin</span>
            <Switch checked={f.is_super_admin} onCheckedChange={(v) => set('is_super_admin', v)} data-testid="cc-new-super" />
          </div>
          <div className="flex items-center justify-between rounded-xl border border-[#E8E5DF] p-3">
            <span className="text-sm">Force credential change on first login</span>
            <Switch checked={f.force_password_change} onCheckedChange={(v) => set('force_password_change', v)} data-testid="cc-new-force" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="cc-new-submit" className="rounded-full bg-[#FF3B6B] text-white">{busy ? 'Creating…' : 'Create User'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ManageUserDialog = ({ user, onClose, onChanged, onEditPermissions }) => {
  const [name, setName] = useState(user?.name || '');
  const [role, setRole] = useState(user?.role || 'employee');
  const [empRole, setEmpRole] = useState(user?.employee_role || 'manager');
  const [isSuper, setIsSuper] = useState(!!user?.is_super_admin);
  const [pin, setPin] = useState(''); const [reason, setReason] = useState('');
  const [busy, setBusy] = useState('');
  const [lastTemp, setLastTemp] = useState(null); // { kind, value }
  const copyTemp = async () => {
    try { await navigator.clipboard.writeText(lastTemp.value); toast.success('Copied to clipboard'); }
    catch { toast.error('Could not copy — select and copy manually'); }
  };
  if (!user) return null;
  const act = async (fn, key) => { setBusy(key); try { await fn(); onChanged?.(); } catch (er) { toast.error(errMsg(er)); } finally { setBusy(''); } };
  const saveDetails = () => act(async () => {
    await axios.put(`${API}/api/admin/control/users/${user.user_id}`, { name, role, employee_role: empRole, is_super_admin: isSuper }, cfg);
    toast.success('User updated');
  }, 'save');
  const resetPw = () => act(async () => { const { data } = await axios.post(`${API}/api/admin/control/users/${user.user_id}/reset-password`, { reason }, cfg); if (data.temp_password) { setLastTemp({ kind: 'password', value: data.temp_password }); } toast.success(data.temp_password ? 'Password reset — temporary password shown below' : 'Password reset & emailed', { duration: 8000 }); }, 'rpw');
  const resetPin = () => act(async () => { const { data } = await axios.post(`${API}/api/admin/control/users/${user.user_id}/reset-pin`, { reason }, cfg); if (data.temp_pin) { setLastTemp({ kind: 'PIN', value: data.temp_pin }); } toast.success(data.temp_pin ? 'PIN reset — temporary PIN shown below' : 'PIN reset & emailed', { duration: 8000 }); }, 'rpin');
  const lock = () => act(async () => { await axios.post(`${API}/api/admin/control/users/${user.user_id}/lock`, {}, cfg); toast.success('Account locked'); }, 'lock');
  const unlock = () => act(async () => { await axios.post(`${API}/api/admin/control/users/${user.user_id}/unlock`, {}, cfg); toast.success('Account unlocked'); }, 'unlock');
  const forceLogout = () => act(async () => { await axios.post(`${API}/api/admin/control/users/${user.user_id}/force-logout`, {}, cfg); toast.success('Sessions ended'); }, 'flo');
  const del = () => act(async () => { await axios.delete(`${API}/api/admin/control/users/${user.user_id}`, { ...cfg, data: { admin_pin: pin, reason } }); toast.success('User deleted'); onClose?.(); }, 'del');

  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg bg-white max-h-[85vh] overflow-y-auto" data-testid="cc-manage-user-dialog">
        <DialogHeader><DialogTitle>{user.name}</DialogTitle><DialogDescription>{user.email} · {user.role}{user.employee_role ? ` / ${user.employee_role}` : ''}</DialogDescription></DialogHeader>

        <div className="space-y-4">
          <div className="rounded-xl border border-[#E8E5DF] p-3 space-y-3">
            <p className="text-xs font-semibold text-[#6B6B7A] uppercase">Details</p>
            <div><Label className="text-xs">Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="cc-edit-name" className="mt-1 rounded-xl" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Role</Label>
                <Select value={role} onValueChange={setRole}><SelectTrigger className="mt-1 rounded-xl" data-testid="cc-edit-role"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="admin">Admin</SelectItem><SelectItem value="employee">Employee</SelectItem></SelectContent></Select>
              </div>
              {role === 'employee' && (
                <div><Label className="text-xs">Employee role</Label>
                  <Select value={empRole} onValueChange={setEmpRole}><SelectTrigger className="mt-1 rounded-xl" data-testid="cc-edit-emprole"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="manager">Manager</SelectItem><SelectItem value="party_host">Party Host</SelectItem><SelectItem value="staff">Staff</SelectItem></SelectContent></Select>
                </div>
              )}
            </div>
            <div className="flex items-center justify-between"><span className="text-sm">Super Admin</span><Switch checked={isSuper} onCheckedChange={setIsSuper} data-testid="cc-edit-super" /></div>
            <Button onClick={saveDetails} disabled={busy === 'save'} data-testid="cc-edit-save" className="w-full rounded-full bg-[#1E1E24] text-white">{busy === 'save' ? 'Saving…' : 'Save Details'}</Button>
            <Button variant="outline" onClick={() => onEditPermissions?.(user)} data-testid="cc-edit-permissions-btn" className="w-full rounded-full"><SlidersHorizontal className="w-4 h-4 mr-1" /> Edit Permissions</Button>
          </div>

          <div className="rounded-xl border border-[#E8E5DF] p-3 space-y-3">
            <p className="text-xs font-semibold text-[#6B6B7A] uppercase">Credential resets <span className="text-[#9CA3AF] normal-case font-normal">(no PIN needed — emailed & shown once)</span></p>
            <div><Label className="text-xs">Reason (optional)</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="cc-manage-reason" className="mt-1 rounded-xl" placeholder="Why? (for the audit log)" /></div>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={resetPw} disabled={busy === 'rpw'} data-testid="cc-reset-pw" className="rounded-full"><KeyRound className="w-3 h-3 mr-1" /> Reset Password</Button>
              <Button variant="outline" onClick={resetPin} disabled={busy === 'rpin'} data-testid="cc-reset-pin" className="rounded-full"><KeyRound className="w-3 h-3 mr-1" /> Reset PIN</Button>
              {user.locked ? (
                <Button variant="outline" onClick={unlock} disabled={busy === 'unlock'} data-testid="cc-unlock" className="rounded-full"><Unlock className="w-3 h-3 mr-1" /> Unlock</Button>
              ) : (
                <Button variant="outline" onClick={lock} disabled={busy === 'lock'} data-testid="cc-lock" className="rounded-full"><Lock className="w-3 h-3 mr-1" /> Lock</Button>
              )}
              <Button variant="outline" onClick={forceLogout} disabled={busy === 'flo'} data-testid="cc-force-logout" className="rounded-full"><LogOut className="w-3 h-3 mr-1" /> Force Logout</Button>
            </div>
            {lastTemp && (
              <div className="rounded-xl border border-[#06D6A0]/40 bg-[#EAF7F2] p-3" data-testid="cc-temp-credential">
                <p className="text-xs text-[#6B6B7A] mb-1">Temporary {lastTemp.kind} — share securely with the user. They'll set a new one at next login.</p>
                <div className="flex items-center gap-2">
                  <span className="flex-1 text-xl font-bold font-mono tracking-widest text-[#1E1E24]" data-testid="cc-temp-credential-value">{lastTemp.value}</span>
                  <Button variant="outline" size="sm" onClick={copyTemp} className="rounded-full" data-testid="cc-temp-credential-copy"><Copy className="w-3 h-3 mr-1" /> Copy</Button>
                </div>
              </div>
            )}
            <div className="pt-1"><Label className="text-xs text-[#FF3B6B]">Delete requires your PIN</Label><Input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))} type="password" inputMode="numeric" data-testid="cc-manage-pin" className="mt-1 rounded-xl font-mono" placeholder="•••• (for delete only)" /></div>
            <Button variant="outline" onClick={del} disabled={busy === 'del'} data-testid="cc-delete-user" className="w-full rounded-full border-red-300 text-red-600 hover:bg-red-50"><Trash2 className="w-3 h-3 mr-1" /> Delete User (soft)</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const UsersTab = ({ onEditPermissions }) => {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]); const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(''); const [createOpen, setCreateOpen] = useState(false);
  const [manage, setManage] = useState(null);
  const [confirmDeact, setConfirmDeact] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [inactivityDays, setInactivityDays] = useState('');
  const [savingInactivity, setSavingInactivity] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/admin/settings/inactivity`, cfg);
        // Only seed the field if the user hasn't started typing yet (avoids a
        // race where a slow GET overwrites what they just entered).
        setInactivityDays((prev) => (prev === '' ? String(data.inactivity_days ?? 30) : prev));
      } catch { /* silent */ }
    })();
  }, []);
  const saveInactivity = async () => {
    const n = parseInt(inactivityDays, 10);
    if (!Number.isInteger(n) || n < 1 || n > 3650) { toast.error('Enter a number of days between 1 and 3650'); return; }
    setSavingInactivity(true);
    try {
      await axios.put(`${API}/api/admin/settings/inactivity`, { inactivity_days: n }, cfg);
      toast.success(`Employees are now auto-deactivated after ${n} days of inactivity`);
    } catch (er) { toast.error(errMsg(er)); } finally { setSavingInactivity(false); }
  };
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/control/users`, { ...cfg, params: search ? { search } : {} });
      setUsers(data.users || []);
    } catch (er) { toast.error(errMsg(er)); } finally { setLoading(false); }
  }, [search]);
  useEffect(() => { load(); }, [load]);

  const setActive = async (u, active) => {
    setTogglingId(u.user_id);
    try {
      await axios.post(`${API}/api/admin/control/users/${u.user_id}/set-active`, { active }, cfg);
      toast.success(active ? `${u.name} activated` : `${u.name} deactivated`);
      await load();
    } catch (er) { toast.error(errMsg(er)); } finally { setTogglingId(null); }
  };

  return (
    <div data-testid="cc-users">
      <div className="mb-4 rounded-2xl border border-[#E8E5DF] bg-white p-4 flex flex-wrap items-center gap-3" data-testid="cc-inactivity-setting">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-[#FF3B6B]" />
          <span className="text-sm text-[#1E1E24]">Auto-deactivate employees after</span>
        </div>
        <Input
          type="number" min={1} max={3650}
          value={inactivityDays}
          onChange={(e) => setInactivityDays(e.target.value.replace(/\D/g, '').slice(0, 4))}
          className="w-20 rounded-xl text-center font-mono"
          data-testid="cc-inactivity-days"
        />
        <span className="text-sm text-[#1E1E24]">days of inactivity</span>
        <Button onClick={saveInactivity} disabled={savingInactivity} data-testid="cc-inactivity-save" className="rounded-full bg-[#1E1E24] text-white">{savingInactivity ? 'Saving…' : 'Save'}</Button>
        <span className="text-xs text-[#6B6B7A] w-full sm:w-auto">Employees blocked past this window can request reactivation from the login screen.</span>
      </div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or email…" data-testid="cc-users-search" className="pl-10 rounded-full" />
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="cc-create-user-btn" className="rounded-full bg-[#FF3B6B] text-white"><UserPlus className="w-4 h-4 mr-1" /> Create User</Button>
        <Button variant="outline" onClick={load} className="rounded-full"><RefreshCw className="w-4 h-4" /></Button>
      </div>

      <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead>User</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead>Active</TableHead><TableHead>PIN</TableHead><TableHead className="text-right">Manage</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-10 text-[#6B6B7A]">Loading…</TableCell></TableRow>
            ) : users.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-10 text-[#6B6B7A]">No users</TableCell></TableRow>
            ) : users.map((u) => (
              <TableRow key={u.user_id} data-testid={`cc-user-row-${u.user_id}`}>
                <TableCell>
                  <div className="font-medium text-[#1E1E24]">{u.name}</div>
                  <div className="text-xs text-[#6B6B7A]">{u.email}{u.employee_number ? ` · ${u.employee_number}` : ''}</div>
                </TableCell>
                <TableCell>
                  <span className="capitalize">{u.role}{u.employee_role ? ` / ${u.employee_role}` : ''}</span>
                  {u.is_super_admin && <Badge className="ml-2 bg-[#FF3B6B] text-white">Super</Badge>}
                </TableCell>
                <TableCell>
                  {u.locked ? <Badge className="bg-red-100 text-red-700">Locked</Badge>
                    : u.active ? <Badge className="bg-green-100 text-green-700">Active</Badge>
                      : <Badge className="bg-gray-200 text-gray-700">Inactive</Badge>}
                </TableCell>
                <TableCell>
                  <Switch
                    checked={!!u.active}
                    disabled={u.is_super_admin || u.user_id === me?.user_id || togglingId === u.user_id}
                    onCheckedChange={(v) => { if (v) { setActive(u, true); } else { setConfirmDeact(u); } }}
                    data-testid={`cc-active-toggle-${u.user_id}`}
                  />
                </TableCell>
                <TableCell>{u.has_pin ? '✓' : '—'}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="outline" onClick={() => setManage(u)} data-testid={`cc-manage-${u.user_id}`} className="rounded-full"><UserCog className="w-3 h-3 mr-1" /> Manage</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <CreateUserDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={load} />
      <ManageUserDialog user={manage} onClose={() => setManage(null)} onChanged={load} onEditPermissions={(u) => { setManage(null); onEditPermissions?.(u); }} />

      <Dialog open={!!confirmDeact} onOpenChange={(o) => !o && setConfirmDeact(null)}>
        <DialogContent className="max-w-md bg-white" data-testid="cc-deactivate-confirm">
          <DialogHeader>
            <DialogTitle>Deactivate {confirmDeact?.name}?</DialogTitle>
            <DialogDescription>
              {confirmDeact?.name} will be signed out immediately and blocked from logging in until an admin reactivates them. Their data and history are kept.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDeact(null)} className="rounded-full">Cancel</Button>
            <Button onClick={() => { const u = confirmDeact; setConfirmDeact(null); setActive(u, false); }} data-testid="cc-deactivate-confirm-btn" className="rounded-full bg-[#FF3B6B] text-white">Deactivate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ─────────────────────────── PERMISSIONS MATRIX ───────────────────────────
const PermissionsTab = ({ initialUser }) => {
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(initialUser?.user_id || '');
  const [groups, setGroups] = useState([]);
  const [matrix, setMatrix] = useState({});
  const [loading, setLoading] = useState(false); const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [uRes, sRes] = await Promise.all([
          axios.get(`${API}/api/admin/control/users`, cfg),
          axios.get(`${API}/api/admin/control/permissions/sections`, cfg),
        ]);
        setUsers(uRes.data.users || []);
        setGroups(sRes.data.groups || []);
      } catch (er) { toast.error(errMsg(er)); }
    })();
  }, []);
  useEffect(() => { if (initialUser?.user_id) setSelected(initialUser.user_id); }, [initialUser]);

  const loadPerms = useCallback(async (uid) => {
    if (!uid) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/control/permissions/${uid}`, cfg);
      setMatrix(data.effective || {});
    } catch (er) { toast.error(errMsg(er)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { if (selected) loadPerms(selected); }, [selected, loadPerms]);

  const toggle = (key, action, val) => setMatrix((p) => {
    const cur = p[key] || { view: false, edit: false };
    const next = { ...cur, [action]: val };
    if (action === 'edit' && val) next.view = true;
    if (action === 'view' && !val) next.edit = false;
    return { ...p, [key]: next };
  });
  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/control/permissions/${selected}`, { permissions: matrix }, cfg);
      toast.success('Permissions saved');
    } catch (er) { toast.error(errMsg(er)); } finally { setSaving(false); }
  };

  const selUser = users.find((u) => u.user_id === selected);
  return (
    <div data-testid="cc-permissions">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="min-w-[260px]">
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger className="rounded-full" data-testid="cc-perm-user-select"><SelectValue placeholder="Select a user…" /></SelectTrigger>
            <SelectContent>{users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name} — {u.email}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {selUser?.is_super_admin && <Badge className="bg-[#FF3B6B] text-white">Super Admin — full access</Badge>}
        <div className="flex-1" />
        <Button onClick={save} disabled={!selected || saving || selUser?.is_super_admin} data-testid="cc-perm-save" className="rounded-full bg-[#FF3B6B] text-white">{saving ? 'Saving…' : 'Save Permissions'}</Button>
      </div>

      {!selected ? (
        <div className="text-center py-16 text-[#6B6B7A] bg-white rounded-2xl border border-[#E8E5DF]">Select a user to edit their access.</div>
      ) : loading ? (
        <div className="text-center py-16 text-[#6B6B7A]">Loading…</div>
      ) : (
        <div className="space-y-5">
          {groups.map((g) => (
            <div key={g.group} className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden">
              <div className="px-4 py-2.5 bg-[#FFF4E6] font-semibold text-sm text-[#1E1E24]">{g.group}</div>
              <div className="divide-y divide-[#F3F0EA]">
                {g.sections.map((s) => {
                  const m = matrix[s.key] || { view: false, edit: false };
                  return (
                    <div key={s.key} className="flex items-center justify-between px-4 py-2.5" data-testid={`cc-perm-row-${s.key}`}>
                      <span className="text-sm text-[#1E1E24]">{s.label}</span>
                      <div className="flex items-center gap-6">
                        <label className="flex items-center gap-2 text-xs text-[#6B6B7A]">View
                          <Switch checked={!!m.view} onCheckedChange={(v) => toggle(s.key, 'view', v)} disabled={selUser?.is_super_admin} data-testid={`cc-perm-${s.key}-view`} /></label>
                        <label className="flex items-center gap-2 text-xs text-[#6B6B7A]">Edit
                          <Switch checked={!!m.edit} onCheckedChange={(v) => toggle(s.key, 'edit', v)} disabled={selUser?.is_super_admin} data-testid={`cc-perm-${s.key}-edit`} /></label>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────── CRM CAPABILITIES ───────────────────────────
const CapabilitiesTab = () => {
  const { checkAuth } = useAuth();
  const [caps, setCaps] = useState({}); const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true); const [saving, setSaving] = useState(false);
  useEffect(() => {
    (async () => {
      try { const { data } = await axios.get(`${API}/api/admin/control/capabilities`, cfg); setCaps(data.capabilities || {}); setGroups(data.groups || []); }
      catch (er) { toast.error(errMsg(er)); } finally { setLoading(false); }
    })();
  }, []);
  const save = async () => {
    setSaving(true);
    try { const { data } = await axios.put(`${API}/api/admin/control/capabilities`, { capabilities: caps }, cfg); setCaps(data.capabilities); toast.success('Capabilities saved'); await checkAuth?.(); }
    catch (er) { toast.error(errMsg(er)); } finally { setSaving(false); }
  };
  if (loading) return <div className="text-center py-16 text-[#6B6B7A]">Loading…</div>;
  return (
    <div data-testid="cc-capabilities">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-[#6B6B7A]">Turn CRM modules on or off. Disabled modules disappear from the sidebar for everyone.</p>
        <Button onClick={save} disabled={saving} data-testid="cc-caps-save" className="rounded-full bg-[#FF3B6B] text-white">{saving ? 'Saving…' : 'Save Capabilities'}</Button>
      </div>
      <div className="space-y-5">
        {groups.map((g) => (
          <div key={g.group} className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden">
            <div className="px-4 py-2.5 bg-[#EAF7F2] font-semibold text-sm text-[#1E1E24]">{g.group}</div>
            <div className="divide-y divide-[#F3F0EA]">
              {g.sections.map((s) => {
                const locked = s.key === 'admin_control';
                return (
                  <div key={s.key} className="flex items-center justify-between px-4 py-2.5" data-testid={`cc-cap-row-${s.key}`}>
                    <span className="text-sm text-[#1E1E24]">{s.label}{locked && <span className="ml-2 text-[10px] text-[#9CA3AF]">(always on)</span>}</span>
                    <Switch checked={caps[s.key] !== false} disabled={locked} onCheckedChange={(v) => setCaps((p) => ({ ...p, [s.key]: v }))} data-testid={`cc-cap-${s.key}`} />
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─────────────────────────── AUDIT LOG ───────────────────────────
const AuditTab = () => {
  const [rows, setRows] = useState([]); const [loading, setLoading] = useState(true); const [search, setSearch] = useState('');
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/api/admin/audit-logs`, { ...cfg, params: { limit: 100, ...(search ? { search } : {}) } });
      setRows(Array.isArray(data) ? data : (data.entries || data.logs || data.items || [])); }
    catch (er) { toast.error(errMsg(er)); } finally { setLoading(false); }
  }, [search]);
  useEffect(() => { load(); }, [load]);
  const exportCsv = () => { window.open(`${API}/api/admin/control/audit/export.csv${search ? `?search=${encodeURIComponent(search)}` : ''}`, '_blank'); };
  return (
    <div data-testid="cc-audit">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search summary…" data-testid="cc-audit-search" className="pl-10 rounded-full" />
        </div>
        <Button variant="outline" onClick={load} className="rounded-full"><RefreshCw className="w-4 h-4" /></Button>
        <Button onClick={exportCsv} data-testid="cc-audit-export" className="rounded-full bg-[#1E1E24] text-white"><Download className="w-4 h-4 mr-1" /> Export CSV</Button>
      </div>
      <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Action</TableHead><TableHead>By</TableHead><TableHead>Summary</TableHead></TableRow></TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={4} className="text-center py-10 text-[#6B6B7A]">Loading…</TableCell></TableRow>
              : rows.length === 0 ? <TableRow><TableCell colSpan={4} className="text-center py-10 text-[#6B6B7A]">No entries</TableCell></TableRow>
                : rows.map((r, i) => (
                  <TableRow key={r.audit_id || i}>
                    <TableCell className="text-xs whitespace-nowrap">{r.created_at ? new Date(r.created_at).toLocaleString() : '—'}</TableCell>
                    <TableCell><Badge variant="outline" className="font-mono text-[11px]">{r.action}</Badge></TableCell>
                    <TableCell className="text-xs">{r.admin_name}</TableCell>
                    <TableCell className="text-xs text-[#1E1E24]">{r.summary}</TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

// ─────────────────────────── SYSTEM HEALTH ───────────────────────────
const HealthTab = () => {
  const [h, setH] = useState(null); const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/api/admin/control/health`, cfg); setH(data); }
    catch (er) { toast.error(errMsg(er)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (loading || !h) return <div className="text-center py-16 text-[#6B6B7A]">Loading…</div>;
  const stat = (label, value) => (
    <div className="bg-white rounded-2xl border border-[#E8E5DF] p-4"><p className="text-xs text-[#6B6B7A]">{label}</p><p className="text-2xl font-semibold text-[#1E1E24] mt-1">{value}</p></div>
  );
  return (
    <div data-testid="cc-health" className="space-y-5">
      <div className="flex justify-end"><Button variant="outline" onClick={load} className="rounded-full"><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button></div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stat('Data Size', fmtBytes(h.db?.data_size))}
        {stat('Storage Size', fmtBytes(h.db?.storage_size))}
        {stat('Objects', (h.db?.objects ?? '—').toLocaleString?.() || h.db?.objects)}
        {stat('Uptime', fmtUptime(h.uptime_seconds))}
      </div>
      <div className="bg-white rounded-2xl border border-[#E8E5DF] p-5">
        <h3 className="font-semibold text-[#1E1E24] mb-3">Collection Counts</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {Object.entries(h.counts || {}).map(([k, v]) => (
            <div key={k} className="rounded-xl bg-[#FFFDF8] border border-[#E8E5DF] p-3" data-testid={`cc-health-count-${k}`}>
              <p className="text-xs text-[#6B6B7A]">{k}</p><p className="font-semibold text-[#1E1E24]">{v < 0 ? '—' : v.toLocaleString()}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-[#E8E5DF] p-5">
        <h3 className="font-semibold text-[#1E1E24] mb-3">Recent Alerts / Errors</h3>
        {(h.recent_errors || []).length === 0 ? <p className="text-sm text-[#6B6B7A]">No recent alerts. 🎉</p>
          : <ul className="space-y-2">{h.recent_errors.map((e, i) => (
            <li key={i} className="text-sm flex gap-2"><ShieldAlert className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" /><span><span className="font-mono text-xs text-[#6B6B7A]">{e.action}</span> — {e.summary}</span></li>
          ))}</ul>}
      </div>
    </div>
  );
};

// ─────────────────────────── DATA CLEANUP ───────────────────────────
const DataCleanupTab = () => {
  const [cats, setCats] = useState([]);
  const [batches, setBatches] = useState([]);
  const [selected, setSelected] = useState({});
  const [pin, setPin] = useState(''); const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(true); const [running, setRunning] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, b] = await Promise.all([
        axios.get(`${API}/api/admin/control/cleanup/preview`, cfg),
        axios.get(`${API}/api/admin/control/cleanup/batches`, cfg),
      ]);
      setCats(p.data.categories || []); setBatches(b.data.batches || []);
    } catch (er) { toast.error(errMsg(er)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const chosen = Object.keys(selected).filter((k) => selected[k]);
  const totalChosen = cats.filter((c) => selected[c.key]).reduce((s, c) => s + c.count, 0);

  const run = async () => {
    setRunning(true); setConfirmOpen(false);
    try {
      const { data } = await axios.post(`${API}/api/admin/control/cleanup/run`, { admin_pin: pin, categories: chosen, reason }, cfg);
      toast.success(`Cleaned ${data.total} record(s)`); setSelected({}); setPin(''); setReason(''); await load();
    } catch (er) { toast.error(errMsg(er, 'Cleanup failed')); } finally { setRunning(false); }
  };
  const undo = async (batchId) => {
    try { const { data } = await axios.post(`${API}/api/admin/control/cleanup/undo/${batchId}`, {}, cfg); toast.success(`Restored ${data.restored} record(s)`); await load(); }
    catch (er) { toast.error(errMsg(er)); }
  };

  if (loading) return <div className="text-center py-16 text-[#6B6B7A]">Loading…</div>;
  return (
    <div data-testid="cc-cleanup" className="space-y-5">
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 flex gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-amber-800">Cleanups are <b>soft deletes</b> — records are hidden but fully reversible from the “Recent cleanups” list below. Requires your 4-digit PIN.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {cats.map((c) => (
          <div key={c.key} className={`rounded-2xl border p-4 transition-colors ${selected[c.key] ? 'border-[#FF3B6B] bg-[#FFF1F5]' : 'border-[#E8E5DF] bg-white'}`} data-testid={`cc-cleanup-card-${c.key}`}>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-[#1E1E24]">{c.label}</h3>
                  <Badge className="bg-[#1E1E24] text-white" data-testid={`cc-cleanup-count-${c.key}`}>{c.count}</Badge>
                </div>
                <p className="text-xs text-[#6B6B7A] mt-1">{c.description}</p>
              </div>
              <Switch checked={!!selected[c.key]} disabled={c.count === 0} onCheckedChange={(v) => setSelected((p) => ({ ...p, [c.key]: v }))} data-testid={`cc-cleanup-toggle-${c.key}`} />
            </div>
            {c.sample?.length > 0 && (
              <ul className="mt-3 space-y-1">
                {c.sample.map((s, i) => (
                  <li key={i} className="text-[11px] text-[#6B6B7A] truncate">• {s.label} <span className="text-[#9CA3AF]">{s.detail}</span></li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-[#E8E5DF] bg-white p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label className="text-xs">Reason (required)</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="cc-cleanup-reason" className="mt-1 rounded-xl" placeholder="e.g. Remove synthetic test data" /></div>
          <div><Label className="text-xs">Your PIN</Label><Input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))} type="password" inputMode="numeric" data-testid="cc-cleanup-pin" className="mt-1 rounded-xl font-mono" placeholder="••••" /></div>
        </div>
        <Button onClick={() => setConfirmOpen(true)} disabled={running || chosen.length === 0 || pin.length !== 4 || !reason.trim()} data-testid="cc-cleanup-run" className="mt-4 w-full rounded-full bg-[#FF3B6B] text-white">
          <Trash2 className="w-4 h-4 mr-1" /> {running ? 'Cleaning…' : `Clean Selected (${totalChosen} records)`}
        </Button>
      </div>

      <div className="rounded-2xl border border-[#E8E5DF] bg-white overflow-hidden">
        <div className="px-4 py-2.5 bg-[#F3F0EA] font-semibold text-sm">Recent cleanups</div>
        {batches.length === 0 ? <p className="p-4 text-sm text-[#6B6B7A]">No cleanups yet.</p> : (
          <Table>
            <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Records</TableHead><TableHead>Reason</TableHead><TableHead className="text-right">Undo</TableHead></TableRow></TableHeader>
            <TableBody>
              {batches.map((b) => (
                <TableRow key={b.batch_id} data-testid={`cc-cleanup-batch-${b.batch_id}`}>
                  <TableCell className="text-xs whitespace-nowrap">{b.created_at ? new Date(b.created_at).toLocaleString() : '—'}</TableCell>
                  <TableCell>{b.total} {b.undone && <Badge className="ml-1 bg-gray-200 text-gray-700">restored</Badge>}</TableCell>
                  <TableCell className="text-xs">{b.reason}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" disabled={b.undone} onClick={() => undo(b.batch_id)} data-testid={`cc-cleanup-undo-${b.batch_id}`} className="rounded-full"><RotateCcw className="w-3 h-3 mr-1" /> Undo</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md bg-white" data-testid="cc-cleanup-confirm">
          <DialogHeader><DialogTitle>Confirm cleanup</DialogTitle><DialogDescription>This will soft-delete <b>{totalChosen}</b> record(s) across {chosen.length} categor(ies). You can undo it afterward.</DialogDescription></DialogHeader>
          <ul className="text-sm space-y-1">{cats.filter((c) => selected[c.key]).map((c) => <li key={c.key}>• {c.label} — <b>{c.count}</b></li>)}</ul>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={run} data-testid="cc-cleanup-confirm-btn" className="rounded-full bg-[#FF3B6B] text-white">Clean Now</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ─────────────────────────── DATABASE OPERATIONS ───────────────────────────
const BackupTab = () => {
  const [backups, setBackups] = useState([]); const [colls, setColls] = useState([]);
  const [storageOn, setStorageOn] = useState(true);
  const [pickColls, setPickColls] = useState({}); const [backupPin, setBackupPin] = useState('');
  const [exportColl, setExportColl] = useState(''); const [exportPin, setExportPin] = useState('');
  const [loading, setLoading] = useState(true); const [busy, setBusy] = useState('');
  const [restore, setRestore] = useState(null); const [restorePin, setRestorePin] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/control/backups`, cfg);
      setBackups(data.backups || []); setColls(data.collections || []); setStorageOn(data.storage_enabled);
      setPickColls((prev) => (Object.keys(prev).length ? prev : Object.fromEntries((data.collections || []).map((c) => [c.name, true]))));
    } catch (er) { toast.error(errMsg(er)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const downloadBlob = async (url, opts, filename) => {
    try {
      const res = await axios({ url: `${API}${url}`, method: opts.method || 'get', data: opts.data, responseType: 'blob', withCredentials: true });
      const href = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = href; a.download = filename; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(href);
    } catch (er) {
      // blob error bodies need decoding
      const text = er?.response?.data ? await er.response.data.text?.().catch(() => '') : '';
      let detail = ''; try { detail = JSON.parse(text)?.detail; } catch (e) { detail = ''; }
      toast.error(detail || errMsg(er, 'Download failed'));
    }
  };

  const createBackup = async () => {
    setBusy('create');
    try {
      const chosen = Object.keys(pickColls).filter((k) => pickColls[k]);
      const { data } = await axios.post(`${API}/api/admin/control/backups`, { admin_pin: backupPin, collections: chosen }, cfg);
      toast.success(`Backup created — ${data.backup.doc_count} docs`); setBackupPin(''); await load();
    } catch (er) { toast.error(errMsg(er, 'Backup failed')); } finally { setBusy(''); }
  };
  const doExport = async () => {
    if (!exportColl) { toast.error('Pick a collection'); return; }
    setBusy('export');
    await downloadBlob('/api/admin/control/export-csv', { method: 'post', data: { admin_pin: exportPin, collection: exportColl } }, `${exportColl}.csv`);
    setExportPin(''); setBusy('');
  };
  const doRestore = async () => {
    setBusy('restore');
    try {
      const { data } = await axios.post(`${API}/api/admin/control/backups/${restore.backup_id}/restore`, { admin_pin: restorePin }, cfg);
      const n = Object.values(data.restored || {}).reduce((a, b) => a + b, 0);
      toast.success(`Restored ${n} record(s)`); setRestore(null); setRestorePin('');
    } catch (er) { toast.error(errMsg(er, 'Restore failed')); } finally { setBusy(''); }
  };

  if (loading) return <div className="text-center py-16 text-[#6B6B7A]">Loading…</div>;
  return (
    <div data-testid="cc-backup" className="space-y-5">
      {!storageOn && <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">Object storage is not configured — backups cannot be saved.</div>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="rounded-2xl border border-[#E8E5DF] bg-white p-5">
          <div className="flex items-center gap-2 mb-3"><HardDrive className="w-4 h-4 text-[#FF3B6B]" /><h3 className="font-semibold text-[#1E1E24]">Create Backup</h3></div>
          <p className="text-xs text-[#6B6B7A] mb-3">A JSON snapshot of the selected collections is saved to object storage.</p>
          <div className="max-h-48 overflow-y-auto rounded-xl border border-[#E8E5DF] divide-y divide-[#F3F0EA] mb-3">
            {colls.map((c) => (
              <label key={c.name} className="flex items-center justify-between px-3 py-2 text-sm cursor-pointer">
                <span>{c.name} <span className="text-[#9CA3AF]">({c.count < 0 ? '—' : c.count})</span></span>
                <Switch checked={pickColls[c.name] !== false} onCheckedChange={(v) => setPickColls((p) => ({ ...p, [c.name]: v }))} data-testid={`cc-backup-coll-${c.name}`} />
              </label>
            ))}
          </div>
          <div className="flex gap-2">
            <Input value={backupPin} onChange={(e) => setBackupPin(e.target.value.replace(/\D/g, '').slice(0, 4))} type="password" inputMode="numeric" placeholder="Your PIN" data-testid="cc-backup-pin" className="rounded-xl font-mono w-32" />
            <Button onClick={createBackup} disabled={busy === 'create' || backupPin.length !== 4} data-testid="cc-backup-create" className="flex-1 rounded-full bg-[#FF3B6B] text-white"><HardDrive className="w-4 h-4 mr-1" /> {busy === 'create' ? 'Backing up…' : 'Create Backup'}</Button>
          </div>
        </div>

        <div className="rounded-2xl border border-[#E8E5DF] bg-white p-5">
          <div className="flex items-center gap-2 mb-3"><Download className="w-4 h-4 text-[#1E1E24]" /><h3 className="font-semibold text-[#1E1E24]">Export Collection to CSV</h3></div>
          <p className="text-xs text-[#6B6B7A] mb-3">Download any collection as a spreadsheet (secrets excluded).</p>
          <Select value={exportColl} onValueChange={setExportColl}>
            <SelectTrigger className="rounded-xl mb-3" data-testid="cc-export-collection"><SelectValue placeholder="Choose a collection…" /></SelectTrigger>
            <SelectContent>{colls.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
          <div className="flex gap-2">
            <Input value={exportPin} onChange={(e) => setExportPin(e.target.value.replace(/\D/g, '').slice(0, 4))} type="password" inputMode="numeric" placeholder="Your PIN" data-testid="cc-export-pin" className="rounded-xl font-mono w-32" />
            <Button onClick={doExport} disabled={busy === 'export' || exportPin.length !== 4 || !exportColl} data-testid="cc-export-run" className="flex-1 rounded-full bg-[#1E1E24] text-white"><Download className="w-4 h-4 mr-1" /> {busy === 'export' ? 'Exporting…' : 'Export CSV'}</Button>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-[#E8E5DF] bg-white overflow-hidden">
        <div className="px-4 py-2.5 bg-[#F3F0EA] font-semibold text-sm flex items-center justify-between">
          <span>Backups</span>
          <Button size="sm" variant="outline" onClick={load} className="rounded-full"><RefreshCw className="w-3 h-3" /></Button>
        </div>
        {backups.length === 0 ? <p className="p-4 text-sm text-[#6B6B7A]">No backups yet.</p> : (
          <Table>
            <TableHeader><TableRow><TableHead>Backup</TableHead><TableHead>Docs</TableHead><TableHead>Size</TableHead><TableHead>Created</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {backups.map((b) => (
                <TableRow key={b.backup_id} data-testid={`cc-backup-row-${b.backup_id}`}>
                  <TableCell className="font-mono text-xs">{b.filename}</TableCell>
                  <TableCell>{b.doc_count}</TableCell>
                  <TableCell>{fmtBytes(b.size_bytes)}</TableCell>
                  <TableCell className="text-xs whitespace-nowrap">{b.created_at ? new Date(b.created_at).toLocaleString() : '—'}</TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button size="sm" variant="outline" onClick={() => downloadBlob(`/api/admin/control/backups/${b.backup_id}/download`, { method: 'get' }, b.filename)} data-testid={`cc-backup-download-${b.backup_id}`} className="rounded-full"><Download className="w-3 h-3 mr-1" /> Download</Button>
                    <Button size="sm" variant="outline" onClick={() => setRestore(b)} data-testid={`cc-backup-restore-${b.backup_id}`} className="rounded-full"><Upload className="w-3 h-3 mr-1" /> Restore</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={!!restore} onOpenChange={(o) => !o && setRestore(null)}>
        <DialogContent className="max-w-md bg-white" data-testid="cc-restore-dialog">
          <DialogHeader><DialogTitle>Restore backup</DialogTitle><DialogDescription>Merges records from <b>{restore?.filename}</b> back into the database (upsert — nothing is dropped). Requires your PIN.</DialogDescription></DialogHeader>
          <Input value={restorePin} onChange={(e) => setRestorePin(e.target.value.replace(/\D/g, '').slice(0, 4))} type="password" inputMode="numeric" placeholder="Your PIN" data-testid="cc-restore-pin" className="rounded-xl font-mono" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRestore(null)} className="rounded-full">Cancel</Button>
            <Button onClick={doRestore} disabled={busy === 'restore' || restorePin.length !== 4} data-testid="cc-restore-confirm" className="rounded-full bg-[#FF3B6B] text-white">{busy === 'restore' ? 'Restoring…' : 'Restore'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ─────────────────────────── PAGE ───────────────────────────
export default function CrmAdminControl() {
  const { isSuperAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('my-account');
  const [permUser, setPermUser] = useState(null);

  useEffect(() => {
    if (!loading && !isSuperAdmin) {
      toast.error('Super Admin access required');
      navigate('/crm', { replace: true });
    }
  }, [loading, isSuperAdmin, navigate]);

  if (loading) return <div className="p-10 text-center text-[#6B6B7A]">Loading…</div>;
  if (!isSuperAdmin) return null;

  const goToPermissions = (u) => { setPermUser(u); setTab('permissions'); };

  const TABS = [
    { v: 'my-account', label: 'My Account', Icon: ShieldCheck },
    { v: 'users', label: 'Users & Access', Icon: Users },
    { v: 'permissions', label: 'Permissions', Icon: SlidersHorizontal },
    { v: 'capabilities', label: 'CRM Capabilities', Icon: Database },
    { v: 'data', label: 'Data Cleanup', Icon: Sparkles },
    { v: 'backup', label: 'Database Ops', Icon: HardDrive },
    { v: 'audit', label: 'Audit Log', Icon: ScrollText },
    { v: 'health', label: 'System Health', Icon: Activity },
  ];

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="crm-admin-control">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-2xl bg-[#FF3B6B]/10 flex items-center justify-center"><ShieldCheck className="w-6 h-6 text-[#FF3B6B]" /></div>
        <div>
          <h1 className="text-2xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>Admin Control Center</h1>
          <p className="text-sm text-[#6B6B7A]">Super-Admin cockpit — users, access, capabilities & system control</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto gap-1 bg-[#F3F0EA] p-1 rounded-2xl mb-5">
          {TABS.map(({ v, label, Icon }) => (
            <TabsTrigger key={v} value={v} data-testid={`cc-tab-${v}`} className="rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <Icon className="w-4 h-4 mr-1.5" /> {label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="my-account"><MyAccountTab /></TabsContent>
        <TabsContent value="users"><UsersTab onEditPermissions={goToPermissions} /></TabsContent>
        <TabsContent value="permissions"><PermissionsTab initialUser={permUser} /></TabsContent>
        <TabsContent value="capabilities"><CapabilitiesTab /></TabsContent>
        <TabsContent value="data"><DataCleanupTab /></TabsContent>
        <TabsContent value="backup"><BackupTab /></TabsContent>
        <TabsContent value="audit"><AuditTab /></TabsContent>
        <TabsContent value="health"><HealthTab /></TabsContent>
      </Tabs>
    </div>
  );
}
